/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import React from 'react';
import { decorate, computed, observable, action, runInAction } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Icon, Popup } from 'semantic-ui-react';
import TimeAgo from 'react-timeago';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import By from '@amzn/base-ui/dist/parts/helpers/By';
import { displaySuccess } from '@amzn/base-ui/dist/helpers/notification';

import KeyPairButtons from './parts/KeyPairButtons';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

class KeyPairCard extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.activeTab = 'public';
    });
  }

  get keyPair() {
    return this.props.keyPair;
  }

  setTab = (tab) => {
    this.activeTab = tab;
  };

  render() {
    const keyPair = this.keyPair;
    const status = keyPair.statusInfo;
    const statusColor = status.color === 'green' ? '#16a34a' : status.color === 'red' ? '#dc2626' : '#6b7280';
    const statusBg = status.color === 'green' ? '#dcfce7' : status.color === 'red' ? '#fee2e2' : '#f3f4f6';

    return (
      <div className="workspace-card">
        <div className="workspace-card-accent" style={{ background: statusColor }} />
        <div className="workspace-card-content">
          {/* Header */}
          <div className="workspace-card-header">
            <div style={{ flex: 1 }}>
              <h3 className="workspace-card-title">{keyPair.name}</h3>
              <div className="workspace-card-description">
                {keyPair.desc || 'No description provided'}
              </div>
            </div>
            <Popup
              trigger={
                <span className="workspace-status-badge" style={{ background: statusBg, color: statusColor }}>
                  <Icon name="circle" style={iconStyle} />
                  {status.display}
                </span>
              }
              content={status.tip}
              size="small"
              inverted
            />
          </div>

          {/* Meta row */}
          <div className="workspace-meta-row">
            <span className="workspace-meta-item">
              <Icon name="clock outline" style={iconStyle} />
              Created <TimeAgo date={keyPair.createdAt} />
            </span>
            <span className="workspace-meta-item">
              <Icon name="user outline" style={iconStyle} />
              <By uid={keyPair.createdBy} />
            </span>
            <span className="workspace-meta-item">
              <Icon name="hashtag" style={iconStyle} />
              {keyPair.id}
            </span>
          </div>

          {/* Tabs */}
          <div className="ssh-key-tabs">
            <button
              className={`ssh-key-tab${this.activeTab === 'public' ? ' active' : ''}`}
              onClick={() => this.setTab('public')}
            >
              Public Key
            </button>
            <button
              className={`ssh-key-tab${this.activeTab === 'private' ? ' active' : ''}`}
              onClick={() => this.setTab('private')}
            >
              Private Key
            </button>
          </div>

          {this.activeTab === 'public' ? this.renderPublicKey(keyPair) : this.renderPrivateKey()}

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <KeyPairButtons keyPair={keyPair} />
          </div>
        </div>
      </div>
    );
  }

  renderPublicKey(keyPair) {
    return (
      <div className="ssh-key-content">
        <div style={{ position: 'relative' }}>
          <textarea
            className="ssh-key-textarea"
            rows={6}
            value={keyPair.publicKey}
            readOnly
          />
          <CopyToClipboard
            text={keyPair.publicKey}
            onCopy={() => displaySuccess('Copied to clipboard', 'Done')}
          >
            <button className="ssh-key-copy-btn" title="Copy to clipboard">
              <Icon name="copy outline" style={iconStyle} />
            </button>
          </CopyToClipboard>
        </div>
      </div>
    );
  }

  renderPrivateKey() {
    return (
      <div className="ssh-key-content ssh-key-private-notice">
        <Icon name="lock" style={{ ...iconStyle, fontSize: '1.5em', color: '#9ca3af' }} />
        <div>
          <div style={{ fontWeight: 600, color: '#374151' }}>Not Available</div>
          <div style={{ fontSize: '0.85em', color: '#6b7280', marginTop: '4px' }}>
            The private key is only available for download at the time of creation. This application does not store it.
          </div>
        </div>
      </div>
    );
  }
}

decorate(KeyPairCard, {
  keyPair: computed,
  activeTab: observable,
  setTab: action,
});

export default inject()(withRouter(observer(KeyPairCard)));
